<script setup lang="ts">
  import { toRef, toRefs } from 'vue';
  import { useField } from 'vee-validate';
  import PhoneInput from './PhoneInput.vue';
  import type {
    FixedVueTelInputResponse,
    PhoneInputProps,
  } from './PhoneInput.types';

  export type PhoneInputFieldProps = PhoneInputProps & {
    rules?: (value: string) => string | boolean;
  };

  const props = withDefaults(defineProps<PhoneInputFieldProps>(), {
    rules: undefined,
  });

  const emit = defineEmits<{
    'update:modelValue': [value: string];
    phoneDetails: [phoneObj: FixedVueTelInputResponse];
  }>();

  const {
    name: inputName,
    label,
    rules,
    ...rest
  } = toRefs<PhoneInputFieldProps>(props);
  const inputProps = toRef(rest);

  const {
    value: inputValue,
    errorMessage,
    handleBlur,
    handleChange,
    meta,
  } = useField<string>(inputName.value, rules?.value, {
    label: label?.value,
  });

  const updateValue = (value: string) => {
    handleChange(value, true);
    emit('update:modelValue', value);
  };

  const forwardPhoneDetails = (phoneObj: FixedVueTelInputResponse) => {
    emit('phoneDetails', phoneObj);
  };
</script>

<template>
  <PhoneInput
    v-bind="inputProps"
    :error="errorMessage as string"
    :label="label"
    :meta="meta"
    :loading="loading"
    :autofocus="autofocus"
    :model-value="inputValue"
    :name="name"
    :input-type="inputType"
    :placeholder="placeholder"
    @blur="handleBlur"
    @update:modelValue="updateValue"
    @phoneDetails="forwardPhoneDetails"
  >
    <template v-if="$slots.iconRight" #iconRight>
      <slot name="iconRight"></slot>
    </template>
  </PhoneInput>
</template>
